import { Button, Modal, TextField, Typography } from '@mui/material'
import { type ChangeEvent, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import * as S from './styles'

interface Props {
	isModalOpened: boolean
	onClose: () => void
	onRejectEmissionRequest: (comment: string) => Promise<void>
}

const RejectEmissionModal: React.FC<Props> = ({
	isModalOpened,
	onClose,
	onRejectEmissionRequest,
}) => {
	const { t } = useTranslation()
	const [comment, setComment] = useState<string>('')
	const [isTryingToCommit, setIsTryingToCommit] = useState(false)

	const trimmedComment = comment.trim()

	const handleFormCommentChange = useCallback(
		(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setComment(event.target.value)
			setIsTryingToCommit(false)
		},
		[]
	)

	const cancelAddProduct = useCallback(() => {
		setIsTryingToCommit(false)
		setComment('')
		onClose()
	}, [onClose])

	const confirmButtonClicked = useCallback(async () => {
		if (!trimmedComment) {
			setIsTryingToCommit(true)
		} else {
			await onRejectEmissionRequest(trimmedComment)
			cancelAddProduct()
		}
	}, [trimmedComment, onRejectEmissionRequest, cancelAddProduct])

	return (
		<Modal open={isModalOpened} onClose={onClose}>
			<S.BackdropWithModalStyles>
				<Typography className="modal-title" variant="h2">
					{t('modal.rejectEmissions.title')}
				</Typography>

				<Typography className="section-title" variant="h6" component="h4">
					{t('modal.rejectEmissions.sectionTitle')}
				</Typography>

				<Typography className="form-label">
					{t('modal.rejectEmissions.commentLabel')}
				</Typography>
				<TextField
					hiddenLabel
					error={isTryingToCommit && !!trimmedComment}
					className="form-input"
					id="product-name-input"
					variant="filled"
					value={comment}
					onChange={handleFormCommentChange}
					size="small"
				/>

				<div className="modal-action-part">
					<Button
						className="modal-cancel-button"
						variant="outlined"
						color="primary"
						onClick={cancelAddProduct}
					>
						{t('button.cancel')}
					</Button>
					<Button
						disabled={comment.length === 0}
						variant="contained"
						color="primary"
						onClick={confirmButtonClicked}
					>
						{t('button.confirm')}
					</Button>
				</div>
			</S.BackdropWithModalStyles>
		</Modal>
	)
}

export { RejectEmissionModal }
